import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import type { ButtonType } from '@/components/custom/arito/custom-input-table/types';
import { AritoIconActionButton } from '../icon-action-button-v2';
import { buttonConfigs, PasteHint } from '../input-table-v2';
import { LoadingSpinner } from '../loading-spinner-v2';
import type { FormMode } from '@/types/form';
import { cn } from '@/lib/utils';

export type InputTableActionBarMoveDirection = 'up' | 'down';
export type InputTableActionBarHandler = () => void | Promise<void>;

export interface InputTableActionBarButton {
  type: ButtonType;
  onClick?: InputTableActionBarHandler;
  disabled?: boolean;
  hidden?: boolean;
  title?: string;
  icon?: number;
  key?: string;
  className?: string;
  iconClassName?: string;
}

export type InputTableActionBarActions = Partial<Record<ButtonType, InputTableActionBarHandler>>;

export interface InputTableActionBarV2Props {
  mode?: FormMode;
  formMode?: FormMode;
  actions?: InputTableActionBarActions;
  buttons?: InputTableActionBarButton[];
  order?: ButtonType[];
  hiddenActions?: ButtonType[];
  viewModeEnabledActions?: ButtonType[];
  disabled?: boolean | ((type: ButtonType) => boolean);
  handleAddRow?: InputTableActionBarHandler;
  handleDeleteRow?: InputTableActionBarHandler;
  handleCopyRow?: InputTableActionBarHandler;
  handlePasteRow?: ((clipboardText?: string) => void | Promise<void>) | InputTableActionBarHandler;
  handleMoveRow?: (direction: InputTableActionBarMoveDirection) => void | Promise<void>;
  handleExport?: InputTableActionBarHandler;
  handlePin?: InputTableActionBarHandler;
  handleRefresh?: InputTableActionBarHandler;
  handleDetail?: InputTableActionBarHandler;
  handleEdit?: InputTableActionBarHandler;
  hanldeEdit?: InputTableActionBarHandler;
  handleViewInventory?: InputTableActionBarHandler;
  handleViewReceipt?: InputTableActionBarHandler;
  handleDownloadExcel?: InputTableActionBarHandler;
  handleUploadExcel?: InputTableActionBarHandler;
  handleSaveAllocation?: InputTableActionBarHandler;
  handleWatch?: InputTableActionBarHandler;
  handleDeleteAllocation?: InputTableActionBarHandler;
  selectedCell?: unknown;
  isPasting?: boolean;
  pasteHintPlaceholder?: string;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  containerClassName?: string;
  innerClassName?: string;
  groupClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
}

const DEFAULT_ORDER: ButtonType[] = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'refresh',
  'detail',
  'edit',
  'viewInventory',
  'viewReceipt',
  'downloadExcel',
  'uploadExcel',
  'saveAllocation',
  'watch',
  'deleteAllocation'
];

const EMPTY_ACTIONS: InputTableActionBarActions = {};

function resolveDisabled(
  type: ButtonType,
  disabled: InputTableActionBarV2Props['disabled'],
  formMode: FormMode | undefined,
  viewModeEnabledActions: ButtonType[] | undefined,
  buttonDisabled: boolean | undefined
) {
  if (buttonDisabled) return true;
  if (typeof disabled === 'function') return disabled(type);
  if (typeof disabled === 'boolean') return disabled;

  return formMode === 'view' && !viewModeEnabledActions?.includes(type);
}

export function InputTableActionBarV2({
  mode,
  formMode,
  actions = EMPTY_ACTIONS,
  buttons,
  order = DEFAULT_ORDER,
  hiddenActions = [],
  viewModeEnabledActions,
  disabled,
  handleAddRow,
  handleDeleteRow,
  handleCopyRow,
  handlePasteRow,
  handleMoveRow,
  handleExport,
  handlePin,
  handleRefresh,
  handleDetail,
  handleEdit,
  hanldeEdit,
  handleViewInventory,
  handleViewReceipt,
  handleDownloadExcel,
  handleUploadExcel,
  handleSaveAllocation,
  handleWatch,
  handleDeleteAllocation,
  selectedCell,
  isPasting,
  pasteHintPlaceholder = 'Dán dữ liệu (Sau khi Ctrl+C từ Excel, Bảng dữ liệu)',
  leftSlot,
  rightSlot,
  containerClassName,
  innerClassName,
  groupClassName,
  buttonClassName,
  iconClassName
}: InputTableActionBarV2Props) {
  const currentMode = mode ?? formMode;
  const [isPasteMenuOpen, setIsPasteMenuOpen] = useState(false);
  const pasteButtonRef = useRef<HTMLSpanElement | null>(null);
  const hasPasteHint = selectedCell !== undefined || isPasting !== undefined;

  const handleOpenPasteMenu = useCallback(() => {
    if (currentMode === 'view') return;
    if (hasPasteHint && !selectedCell) return;

    setIsPasteMenuOpen(true);
  }, [currentMode, hasPasteHint, selectedCell]);

  const handleClosePasteMenu = useCallback(() => {
    setIsPasteMenuOpen(false);
  }, []);

  const handlePasteFromClipboard = useCallback(
    (clipboardText: string) => {
      void handlePasteRow?.(clipboardText);
      handleClosePasteMenu();
    },
    [handleClosePasteMenu, handlePasteRow]
  );

  const resolvedActions = useMemo<InputTableActionBarActions>(
    () => ({
      ...actions,
      add: actions.add ?? handleAddRow,
      delete: actions.delete ?? handleDeleteRow,
      copy: actions.copy ?? handleCopyRow,
      paste: actions.paste ?? (hasPasteHint ? handleOpenPasteMenu : (handlePasteRow as InputTableActionBarHandler)),
      moveUp: actions.moveUp ?? (handleMoveRow ? () => handleMoveRow('up') : undefined),
      moveDown: actions.moveDown ?? (handleMoveRow ? () => handleMoveRow('down') : undefined),
      export: actions.export ?? handleExport,
      pin: actions.pin ?? handlePin,
      refresh: actions.refresh ?? handleRefresh,
      detail: actions.detail ?? handleDetail,
      edit: actions.edit ?? handleEdit ?? hanldeEdit,
      viewInventory: actions.viewInventory ?? handleViewInventory,
      viewReceipt: actions.viewReceipt ?? handleViewReceipt,
      downloadExcel: actions.downloadExcel ?? handleDownloadExcel,
      uploadExcel: actions.uploadExcel ?? handleUploadExcel,
      saveAllocation: actions.saveAllocation ?? handleSaveAllocation,
      watch: actions.watch ?? handleWatch,
      deleteAllocation: actions.deleteAllocation ?? handleDeleteAllocation
    }),
    [
      actions,
      handleAddRow,
      handleCopyRow,
      handleDeleteAllocation,
      handleDeleteRow,
      handleDetail,
      handleDownloadExcel,
      handleEdit,
      hanldeEdit,
      handleExport,
      handleMoveRow,
      handleOpenPasteMenu,
      handlePasteRow,
      handlePin,
      handleRefresh,
      handleSaveAllocation,
      handleUploadExcel,
      handleViewInventory,
      handleViewReceipt,
      handleWatch,
      hasPasteHint
    ]
  );

  const resolvedButtons = useMemo(() => {
    const customButtons = buttons ?? [];
    const customButtonTypes = new Set(customButtons.map(button => button.type));
    const actionButtons = order
      .filter(type => !hiddenActions.includes(type))
      .filter(type => !customButtonTypes.has(type))
      .filter(type => resolvedActions[type])
      .map<InputTableActionBarButton>(type => ({ type, onClick: resolvedActions[type] }));

    return [...actionButtons, ...customButtons].filter(button => !button.hidden);
  }, [buttons, hiddenActions, order, resolvedActions]);

  return (
    <>
      <div className={cn('flex flex-none items-center justify-between bg-white px-1 py-0', containerClassName)}>
        {leftSlot}
        <div className={cn('flex h-full flex-1 items-center justify-between', innerClassName)}>
          <div className={cn('flex items-center', groupClassName)}>
            {resolvedButtons.map((button, index) => {
              const config = buttonConfigs[button.type] ?? { icon: 0, title: '' };
              const isPasteButton = button.type === 'paste';

              return (
                <span
                  key={button.key ?? `${button.type}-${index}`}
                  ref={isPasteButton ? pasteButtonRef : null}
                  className={isPasteButton ? 'relative' : undefined}
                >
                  <AritoIconActionButton
                    title={button.title ?? config.title}
                    icon={button.icon ?? config.icon}
                    onClick={() => void button.onClick?.()}
                    disabled={resolveDisabled(
                      button.type,
                      disabled,
                      currentMode,
                      viewModeEnabledActions,
                      button.disabled
                    )}
                    className={cn(buttonClassName, button.className)}
                    iconClassName={cn(iconClassName, button.iconClassName)}
                  />
                </span>
              );
            })}
            {isPasting && <LoadingSpinner size={20} className='ml-1 border-[#023e8a]' />}
          </div>
          {rightSlot}
        </div>
      </div>

      {hasPasteHint && (
        <PasteHint
          open={isPasteMenuOpen}
          anchorRect={pasteButtonRef.current?.getBoundingClientRect() || null}
          triggerRef={pasteButtonRef}
          onPaste={handlePasteFromClipboard}
          onClose={handleClosePasteMenu}
          placeholder={pasteHintPlaceholder}
        />
      )}
    </>
  );
}

export default InputTableActionBarV2;
